// Theme tokens for both chart and shadow-DOM chrome.
//
// Series colors are fixed by the operator's visual spec: our line charcoal
// #2F2F2F, network median #1f77b4, band amber (fill hue distinct from edge
// ink, see bandEdge below), net-incl-MEV annotation violet #7C3AED. Alpha
// and edge treatment scale with mark size (8% gated wash, 15%/14% band
// fill, 35% whisker/strip fill); hue never changes within a theme.
//
// Band re-ink (occlusion-fix pass): the hero's panel-color casing series
// that used to erase the band edges at their coincidence point is deleted
// outright (chart-options.ts z-ladder); findability instead comes from ink
// and z-order. Fill and edge are separate tokens because light-theme
// #FFD700 measures ~1.4:1 on a white panel (invisible) even with z fixed:
// bandEdge is #A16207 in light (4.9:1 against panelBg, clears the WCAG
// 1.4.11 non-text 3:1 floor with margin), #27865A in dark (8.04:1 against
// the dark panel). band (fill hue) is #D97706 in light,
// #27865A in dark; bandFillOpacity is 0.24 (light) / 0.32 (dark; ribbon
// design, operator direction: the top-decile range reads as a FAT RANGED
// LINE — on live data the hero IS the cluster max, so it rides the
// ribbon's upper edge; the ribbon must be unmistakable, not a wash) — above the
// 0.08 substrate ceiling (it is an encoding, not substrate) and below 0.2 so
// it never out-inks the gap-fill wash.
//
// The dark theme keeps the same roles but lightens the two line series
// (charcoal is invisible on a dark surface); both palettes pass the
// CVD-separation check (worst adjacent pair deltaE > 43). netLine measures
// >=4.5:1 against panelBg in both themes (light 5.70:1, dark 5.65:1) — see
// theme.test.ts.

export type ThemeName = "light" | "dark";

export interface ThemeTokens {
  name: ThemeName;
  ourLine: string;
  medianLine: string;
  /** Band fill hue (area + whisker/strip fill); see bandEdge for the edge-line ink. */
  band: string;
  bandFillOpacity: number;
  /** 1px band edge (p10/p90) ink, distinct from the fill hue — see theme.ts header comment. */
  bandEdge: string;
  /**
   * p90-100 region fill on the PERCENTILE plots only (consistency strip /
   * position view). Light theme uses a paler, more-yellow tint than `band`:
   * translucent #D97706 over white blends to a brown that the operator
   * rejected; #FDE68A at 0.4 blends to a straw highlight (~rgb(254,245,203)).
   * Dark theme matches band/bandFillOpacity (already reads correctly). The
   * main APY chart's band fill is intentionally NOT changed.
   */
  percentileBand: string;
  percentileBandFillOpacity: number;
  /**
   * Net (incl. MEV) annotation hue: the secondary-row/dotted-line/tooltip
   * color whenever the net figure rides alongside the base (charcoal) hero
   * line — never used for a hero/primary series (see chart-options.ts).
   */
  netLine: string;
  /** Gap-fill wash between the our-base and median-base lines (charcoal, low alpha; see chart-options.ts gapFillSeries). */
  gapFillOpacity: number;
  text: string;
  textMuted: string;
  axisLine: string;
  splitLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  statsText: string;
  /** Tick label / axis name color: darker (more legible) than textMuted. */
  axisText: string;
  /** Opaque plot/legend/panel background — pure white (light) / dark panel (dark). */
  panelBg: string;
  /** 1px border for hairlines (header, answer band, pin lane). */
  frameBorder: string;
  /** Drop-shadow color for the docked pin card. */
  frameShadow: string;
  /**
   * Strip/whisker gold fill at binding-encoding alpha (0.35) — distinct from
   * the low-alpha band wash (bandFillOpacity 0.2); same hue, alpha scales
   * with mark size per the operator's ink-identity rule.
   */
  goldMarkAlpha: number;
  /**
   * 1px hairline outline for the strip's gold segment and the per-epoch
   * whisker: tuned to reach >=3:1 contrast against panelBg in both themes
   * (gold fill alone cannot). Carries the boundary read; gold carries
   * identity.
   */
  boundaryOutline: string;
  /** Amber: pin selection axis tick and pin-card left edge (a3mc selection semantic). */
  amber: string;
  /** 8% alpha wash of the muted token for the coverage-gated markArea. */
  gatedWash: string;
  /** 1px 45-degree hatch stroke, theme-tuned opacity (light ~0.18, dark ~0.22). */
  gatedHatch: string;
  /**
   * Policy-state green: a measured-zero commission reads as a delegator-
   * facing fact, not an error — restrained emerald, AA (>=4.5:1) against
   * panelBg in both themes for a 12px/400 note (see theme.test.ts).
   */
  policyNote: string;
  /**
   * Income channel inks (income-view channel lines, explorer level-1
   * stacked bars, level-2 sum bars, tooltip swatches). Introduced as
   * aliases of the series tokens they visually matched (fees = medianLine,
   * tips = bandEdge, MEV = netLine, BAM = policyNote) but semantically
   * independent — a retune of either side must not silently recolor the
   * other. Dark tips/bam diverged from their aliases in the 2026-07-14
   * validator retune (see DARK below). Deliberately excluded from the
   * theme.test.ts SERIES_HUE_KEYS distinctness loop: some still duplicate
   * values by construction.
   */
  channelFees: string;
  channelTips: string;
  /** Block-observed tips witness: a yellowish SIBLING of channelTips —
   * deliberately close (two measurements of one flow, differentiated by
   * dash + weight + a small hue step), never a distinct-entity hue. */
  channelTipsObserved: string;
  channelMev: string;
  channelBam: string;
  /**
   * dataZoom slider track / selected-window chrome. Aliases of the
   * gatedWash/gatedHatch values at introduction, but the slider means
   * "your selected range", not "cluster statistics withheld" — separate
   * tokens so either meaning can re-ink without dragging the other along.
   */
  sliderTrack: string;
  sliderFiller: string;
}

// Light chrome retune (2026-07-13): the previous light neutrals were exactly
// achromatic (R=G=B) while the dark reference's panel chrome carries a cool
// blue-gray cast — the retuned neutrals join one cool family at hairline-
// equal weights. Measured fixes: axisLine #9a9a9a was 2.81:1 on white, below
// the WCAG 1.4.11 non-text 3:1 floor this file enforces on bandEdge —
// #868E9B measures 3.30:1; gapFillOpacity 0.06 blended charcoal-over-white
// to #f3f3f3, LIGHTER than the gridlines under it (the encoding was
// out-inked by its substrate) — 0.10 matches dark and restores the
// relationship. Series inks and panelBg (the paper stays white) unchanged.
export const LIGHT: ThemeTokens = {
  name: "light",
  ourLine: "#2F2F2F",
  medianLine: "#1f77b4",
  band: "#D97706",
  bandFillOpacity: 0.24,
  bandEdge: "#A16207",
  percentileBand: "#FDE68A",
  percentileBandFillOpacity: 0.4,
  netLine: "#7C3AED",
  gapFillOpacity: 0.1,
  text: "#16181D",
  textMuted: "#5B6472",
  axisLine: "#868E9B",
  splitLine: "#E4E7EC",
  tooltipBg: "#ffffff",
  tooltipBorder: "#D2D7DE",
  statsText: "#3F444D",
  axisText: "#343A43",
  panelBg: "#ffffff",
  frameBorder: "#C2C8D1",
  frameShadow: "rgba(0, 0, 0, 0.15)",
  goldMarkAlpha: 0.35,
  boundaryOutline: "#343A43",
  // Selection ink: #C2410C (5.18:1 on white) — burnt orange, deliberately
  // distant from bandEdge #A16207 (deltaE 30.1; the old #a3670b sat at
  // deltaE 2.4 from it, one ink for two unrelated meanings).
  amber: "#C2410C",
  gatedWash: "rgba(91, 100, 114, 0.08)",
  gatedHatch: "rgba(91, 100, 114, 0.18)",
  policyNote: "#047857",
  channelFees: "#1f77b4",
  channelTips: "#A16207",
  channelTipsObserved: "#B8860B",
  channelMev: "#7C3AED",
  channelBam: "#047857",
  sliderTrack: "rgba(91, 100, 114, 0.08)",
  sliderFiller: "rgba(91, 100, 114, 0.18)",
};

export const DARK: ThemeTokens = {
  name: "dark",
  // Hero thread: near-white on the dark panel. Operator law: red/magenta
  // family is banned for the hero — on a financial chart red-family ink
  // reads as loss ("don't scare people"), regardless of its chromatic
  // contrast advantages inside the green ribbon.
  ourLine: "#E8E8E8",
  medianLine: "#4C9BD4",
  // Leaf-green band field (dark, operator: "darker, as leaves"): #27865A;
  // medianLine blue (#4C9BD4, hue 205, deltaE 38.8) and policyNote green
  // (#34D399, hue 158, deltaE 38.0); 8.04:1 on the dark panel. Replaces
  // the gold family whose 0.18 wash blended muddy-olive over the cool
  // panel.
  band: "#27865A",
  bandFillOpacity: 0.32,
  bandEdge: "#27865A",
  percentileBand: "#27865A",
  percentileBandFillOpacity: 0.14,
  netLine: "#A78BFA",
  gapFillOpacity: 0.1,
  text: "#e6e6e6",
  textMuted: "#9a9a9a",
  axisLine: "#6f6f6f",
  splitLine: "#333333",
  tooltipBg: "#22252a",
  tooltipBorder: "#3a3f45",
  statsText: "#b8b8b8",
  axisText: "#cfcfcf",
  panelBg: "#22252a",
  frameBorder: "#454b52",
  frameShadow: "rgba(0, 0, 0, 0.55)",
  goldMarkAlpha: 0.35,
  boundaryOutline: "#d8d8d8",
  amber: "#e0a83e",
  gatedWash: "rgba(154, 154, 154, 0.08)",
  gatedHatch: "rgba(154, 154, 154, 0.22)",
  policyNote: "#34D399",
  channelFees: "#4C9BD4",
  // channelTips/channelBam retuned 2026-07-14 for the income view's
  // channel LINES (validate_palette.js vs panel #22252a): the former gold
  // #FFD700 is banned as a dark-panel line ink (operator color law) and
  // sat outside the dark lightness band (OKLCH L 0.887 > 0.67), and the
  // former #34D399 alias sat at L 0.773. The chromatic channel triple
  // #4C9BD4 / #C9722E / #33A876 passes all validator checks (worst
  // adjacent pair deltaE 27.5 protan). Explorer bars and tooltip swatches
  // re-ink with the tokens by construction (one token per channel).
  channelTips: "#C9722E",
  channelTipsObserved: "#DDA33E",
  channelMev: "#A78BFA",
  channelBam: "#33A876",
  sliderTrack: "rgba(154, 154, 154, 0.08)",
  sliderFiller: "rgba(154, 154, 154, 0.22)",
};

export function themeTokens(name: string | null): ThemeTokens {
  return name === "dark" ? DARK : LIGHT;
}
